import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

interface TweetCardProps {
  author: string;
  avatar?: string;
  content: string;
  style: {
    entrance: "flip3D";
    theme?: string;
    highlightColor?: string;
  };
}

/** 逐词高亮 */
function highlightWords(
  text: string,
  highlightColor: string,
  currentWord: number,
): React.ReactNode {
  const words = text.split(" ");
  return (
    <span style={{ lineHeight: 1.5 }}>
      {words.map((word, i) => (
        <span
          key={i}
          style={{
            color: i <= currentWord ? highlightColor : "rgba(255,255,255,0.85)",
            textShadow:
              i <= currentWord ? `0 0 20px ${highlightColor}60` : "none",
            transition: "color 0.2s, text-shadow 0.2s",
            marginRight: 6,
          }}
        >
          {word}
        </span>
      ))}
    </span>
  );
}

/** SVG 图标组件 */
const IconRow: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const icons = [
    { label: "♥", delay: 0 },
    { label: "↻", delay: 8 },
    { label: "💬", delay: 16 },
  ];

  return (
    <div
      style={{
        display: "flex",
        gap: 40,
        marginTop: 30,
        justifyContent: "center",
      }}
    >
      {icons.map((icon, i) => {
        const localFrame = Math.max(0, frame - icon.delay);
        const progress = spring({
          frame: localFrame,
          fps,
          config: { damping: 12, mass: 0.6, stiffness: 150 },
          durationInFrames: 15,
        });
        const opacity = interpolate(progress, [0, 0.3, 1], [0, 1, 1]);
        const scale = interpolate(progress, [0, 1], [0.5, 1]);

        return (
          <div
            key={i}
            style={{
              fontSize: 28,
              color: "rgba(255,255,255,0.5)",
              opacity,
              transform: `scale(${scale})`,
              display: "flex",
              alignItems: "center",
              gap: 6,
              cursor: "default",
            }}
          >
            <span>{icon.label}</span>
            <span style={{ fontSize: 14, fontFamily: "sans-serif" }}>
              {["42", "128", "18"][i]}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const TweetCard: React.FC<TweetCardProps> = ({
  author,
  avatar,
  content,
  style,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Flip3D 翻转入场
  const flipProgress = spring({
    frame,
    fps,
    config: { damping: 14, mass: 0.8, stiffness: 80 },
    durationInFrames: 40,
  });

  const rotateY = interpolate(flipProgress, [0, 1], [90, 0]);
  const opacity = interpolate(flipProgress, [0, 0.2, 1], [0, 1, 1]);
  const scale = interpolate(flipProgress, [0, 0.5, 1], [0.8, 1.05, 1]);

  // 逐词高亮进度
  const wordCount = content.split(" ").length;
  const wordDuration = 8; // 每个词 8 帧
  const currentWord = Math.min(
    Math.floor(Math.max(0, frame - 30) / wordDuration),
    wordCount - 1,
  );

  const themeColor = style.theme || "#00F5FF";

  return (
    <AbsoluteFill
      style={{
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#ffffff",
      }}
    >
      <div
        style={{
          perspective: 1200,
          width: "80%",
          maxWidth: 800,
        }}
      >
        <div
          style={{
            transform: `rotateY(${rotateY}deg) scale(${scale})`,
            opacity,
            background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
            borderRadius: 20,
            padding: "40px 50px",
            border: `2px solid ${themeColor}40`,
            boxShadow: `0 0 40px ${themeColor}20, inset 0 0 60px rgba(0,0,0,0.3)`,
          }}
        >
          {/* 头像 + 作者 */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              marginBottom: 24,
            }}
          >
            {/* 头像占位 — 用首字母圆环 */}
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${themeColor}, ${themeColor}80)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
                fontWeight: 700,
                color: "#fff",
                fontFamily: "sans-serif",
                boxShadow: `0 0 20px ${themeColor}40`,
              }}
            >
              {author.charAt(0).toUpperCase()}
            </div>
            <div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: "#ffffff",
                  fontFamily: "sans-serif",
                }}
              >
                {author}
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: "rgba(255,255,255,0.4)",
                  fontFamily: "sans-serif",
                }}
              >
                @{author.toLowerCase().replace(/[^a-z0-9]/g, "")}
              </div>
            </div>
          </div>

          {/* 推文内容 — 逐词高亮 */}
          <div
            style={{
              fontSize: 32,
              fontWeight: 600,
              fontFamily: "sans-serif",
              lineHeight: 1.5,
              marginBottom: 20,
            }}
          >
            {highlightWords(content, style.highlightColor || "#FFD700", currentWord)}
          </div>

          {/* 底部交互图标 */}
          <IconRow frame={Math.max(0, frame - 20)} fps={fps} />
        </div>
      </div>
    </AbsoluteFill>
  );
};

export default TweetCard;
;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
